import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Settings, Languages, Film, Eraser, Save, Mail, Tv, Trash2, Loader2, Plus, EyeOff } from "lucide-react";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { DEVICES, DEFAULT_DEVICE_ID, loadDeviceId, saveDeviceId } from "@/lib/deviceProfiles";
import IptvSourcesSection from "@/components/IptvSourcesSection";

const LANGUAGES = [
  { value: "ALL",        label: "Any language",       flag: "🌐" },
  { value: "Multi",      label: "Multi / Dual audio", flag: "🌐" },
  { value: "English",    label: "English",            flag: "🇬🇧" },
  { value: "Spanish",    label: "Spanish",            flag: "🇪🇸" },
  { value: "French",     label: "French",             flag: "🇫🇷" },
  { value: "German",     label: "German",             flag: "🇩🇪" },
  { value: "Italian",    label: "Italian",            flag: "🇮🇹" },
  { value: "Russian",    label: "Russian",            flag: "🇷🇺" },
  { value: "Japanese",   label: "Japanese",           flag: "🇯🇵" },
  { value: "Korean",     label: "Korean",             flag: "🇰🇷" },
  { value: "Hindi",      label: "Hindi",              flag: "🇮🇳" },
  { value: "Chinese",    label: "Chinese",            flag: "🇨🇳" },
  { value: "Portuguese", label: "Portuguese",         flag: "🇵🇹" },
  { value: "Turkish",    label: "Turkish",            flag: "🇹🇷" },
  { value: "Arabic",     label: "Arabic",             flag: "🇸🇦" },
  { value: "Polish",     label: "Polish",             flag: "🇵🇱" },
  { value: "Dutch",      label: "Dutch",              flag: "🇳🇱" },
  { value: "Swedish",    label: "Swedish",            flag: "🇸🇪" },
];

const CODECS = [
  { value: "ALL",   label: "Any codec",                 hint: "" },
  { value: "HEVC",  label: "HEVC / H.265",              hint: "Smaller files, Apple TV / modern players" },
  { value: "H.264", label: "H.264 / AVC",               hint: "Most compatible, plays everywhere" },
  { value: "AV1",   label: "AV1",                       hint: "Newest, best efficiency" },
  { value: "XviD",  label: "XviD / DivX",               hint: "Older codec, very small file sizes" },
];

function read(key, fallback) {
  try { return localStorage.getItem(key) || fallback; } catch { return fallback; }
}

function write(key, value) {
  try {
    if (value === null || value === undefined) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch { /* no-op */ }
}

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lang, setLang] = useState(() => read("torhub:langFilter", "ALL"));
  const [codec, setCodec] = useState(() => read("torhub:codecFilter", "ALL"));
  const [autoHevc, setAutoHevc] = useState(() => read("torhub:codecFilter", "ALL") === "HEVC");
  // Quality floor — hide trash rips before they ever clutter a list. CAM/TS
  // defaults to ON because nobody actually wants a cinema-cam rip; SD
  // defaults to OFF because some users specifically want small files.
  const [hideCam, setHideCam] = useState(() => read("torhub:hideCam", "1") !== "0");
  const [hideSd, setHideSd] = useState(() => read("torhub:hideSd", "0") === "1");
  // "Best for your device" picker — drives the green ribbon on the
  // recommended torrent in every list.
  const [deviceId, setDeviceId] = useState(() => loadDeviceId());
  const [dirty, setDirty] = useState(false);
  const [digestOptIn, setDigestOptIn] = useState(null); // null = loading
  const [digestBusy, setDigestBusy] = useState(false);

  useEffect(() => {
    if (autoHevc && codec !== "HEVC") setCodec("HEVC");
    if (!autoHevc && codec === "HEVC") setCodec("ALL");
  }, [autoHevc]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user) return;
    api.get("/me/email-prefs")
      .then((r) => setDigestOptIn(Boolean(r.data?.email_digest_opt_in)))
      .catch(() => setDigestOptIn(false));
  }, [user]);

  const toggleDigest = async (next) => {
    setDigestBusy(true);
    try {
      const { data } = await api.put("/me/email-prefs", { email_digest_opt_in: next });
      setDigestOptIn(Boolean(data?.email_digest_opt_in));
      toast.success(next ? "You're subscribed to the Weekly Digest 🎉" : "Unsubscribed from the Weekly Digest");
    } catch {
      toast.error("Could not update preference");
    } finally {
      setDigestBusy(false);
    }
  };

  const [devices, setDevices] = useState(null);
  const [devicesBusy, setDevicesBusy] = useState(false);
  const loadDevices = async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/devices/mine");
      setDevices(data || []);
    } catch {
      setDevices([]);
    }
  };
  useEffect(() => { loadDevices(); }, [user]); // eslint-disable-line
  const revokeDevice = async (id, name) => {
    if (!window.confirm(`Sign out "${name || "this device"}"? It will need to be re-paired to access TorHub.`)) return;
    setDevicesBusy(true);
    try {
      await api.post("/devices/revoke", { device_id: id });
      toast.success("Device signed out");
      await loadDevices();
    } catch {
      toast.error("Could not revoke device");
    } finally {
      setDevicesBusy(false);
    }
  };

  const save = () => {
    write("torhub:langFilter", lang);
    write("torhub:codecFilter", codec);
    write("torhub:hideCam", hideCam ? "1" : "0");
    write("torhub:hideSd",  hideSd  ? "1" : "0");
    saveDeviceId(deviceId);
    setDirty(false);
    toast.success("Preferences saved");
  };

  const resetAll = () => {
    try {
      const keys = Object.keys(localStorage).filter((k) => k.startsWith("torhub:"));
      keys.forEach((k) => localStorage.removeItem(k));
    } catch { /* no-op */ }
    setLang("ALL");
    setCodec("ALL");
    setAutoHevc(false);
    setHideCam(true);
    setHideSd(false);
    setDeviceId(DEFAULT_DEVICE_ID);
    setDirty(false);
    toast.success("All preferences cleared");
  };

  const onLang = (v) => { setLang(v); setDirty(true); };
  const onCodec = (v) => { setCodec(v); setDirty(true); };
  const onAutoHevc = (v) => { setAutoHevc(v); setDirty(true); };
  const onHideCam = (v) => { setHideCam(v); setDirty(true); };
  const onHideSd  = (v) => { setHideSd(v);  setDirty(true); };
  const onDevice  = (v) => { setDeviceId(v); setDirty(true); };

  return (
    <div className="flex h-screen overflow-hidden bg-black text-white">
      <Sidebar />
      <div className="flex-1 h-full overflow-y-auto flex flex-col">
        <Header />
        <main className="px-4 md:px-8 py-6 flex-1 max-w-3xl mx-auto w-full">
          <Link to="/" data-testid="settings-back-home" className="text-[var(--tt-text-muted)] hover:text-white flex items-center gap-2 text-sm mb-6">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>

          <header className="mb-8">
            <h1 className="font-display text-3xl md:text-4xl font-extrabold flex items-center gap-3">
              <Settings className="w-7 h-7 text-[var(--tt-primary)]" /> Settings
            </h1>
            <p className="text-sm text-[var(--tt-text-muted)] mt-2 max-w-2xl">
              These preferences are saved on this device and applied automatically every time you open a movie or show. They never leave your browser.
            </p>
          </header>

          {/* "Best for your device" recommendation picker */}
          <section
            className="mb-8 bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl p-5"
            data-testid="settings-device-card"
          >
            <h2 className="font-display text-lg font-bold mb-1 flex items-center gap-2">
              <Tv className="w-5 h-5 text-emerald-400" /> Your device
            </h2>
            <p className="text-xs text-[var(--tt-text-muted)] mb-4">
              Tell us what you watch on and we'll auto-highlight the single torrent that's the best fit — right resolution, codec, HDR &amp; audio for your hardware. No decision fatigue.
            </p>

            <div
              className="grid grid-cols-2 sm:grid-cols-3 gap-2"
              data-testid="settings-device-grid"
            >
              {DEVICES.map((d) => {
                const Icon = d.icon || Tv;
                const active = deviceId === d.id;
                return (
                  <button
                    key={d.id}
                    type="button"
                    data-testid={`settings-device-${d.id}`}
                    onClick={() => onDevice(d.id)}
                    title={d.description}
                    className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg border text-left transition-colors ${
                      active
                        ? "bg-emerald-400/15 border-emerald-400 text-emerald-300"
                        : "bg-[var(--tt-surface-2)] border-[var(--tt-border)] text-[var(--tt-text)] hover:border-emerald-400/50"
                    }`}
                  >
                    <Icon className={`w-4 h-4 flex-none mt-0.5 ${active ? "text-emerald-300" : "text-[var(--tt-text-muted)]"}`} />
                    <div className="min-w-0">
                      <div className="text-sm font-bold leading-tight">{d.label}</div>
                      <div className="text-[10px] text-[var(--tt-text-muted)] mt-0.5 leading-snug">
                        {d.description}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Default audio language */}
          <section className="mb-8 bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl p-5" data-testid="settings-language-card">
            <h2 className="font-display text-lg font-bold mb-1 flex items-center gap-2">
              <Languages className="w-5 h-5 text-[var(--tt-primary)]" /> Default audio language
            </h2>
            <p className="text-xs text-[var(--tt-text-muted)] mb-4">
              The torrent list will auto-filter to this language on every title. Picks fall back to "Any" if no torrents match.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="settings-language-grid">
              {LANGUAGES.map((l) => (
                <button
                  key={l.value}
                  type="button"
                  data-testid={`settings-lang-${l.value.toLowerCase()}`}
                  onClick={() => onLang(l.value)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                    lang === l.value
                      ? "bg-[var(--tt-primary)] border-[var(--tt-primary)] text-white"
                      : "bg-[var(--tt-surface-2)] border-[var(--tt-border)] text-[var(--tt-text)] hover:border-[var(--tt-primary)]/50"
                  }`}
                >
                  <span className="text-base">{l.flag}</span>
                  <span className="truncate">{l.label}</span>
                </button>
              ))}
            </div>
          </section>

          {/* Email digest opt-in (only for logged-in users) */}
          {user && (
            <section className="mb-8 bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl p-5" data-testid="settings-email-card">
              <h2 className="font-display text-lg font-bold mb-1 flex items-center gap-2">
                <Mail className="w-5 h-5 text-[var(--tt-primary)]" /> Weekly Trending Digest
              </h2>
              <p className="text-xs text-[var(--tt-text-muted)] mb-4">
                Every Monday at 09:00 UTC we&apos;ll email you a short, beautiful round-up of the week&apos;s hottest movies &amp; TV — plus reminders from your watchlist. One click to unsubscribe at any time.
              </p>

              <label
                className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg bg-[var(--tt-surface-2)] border border-[var(--tt-border)] cursor-pointer hover:border-[var(--tt-primary)]/50 transition-colors"
                data-testid="settings-digest-toggle-row"
              >
                <div className="min-w-0">
                  <div className="text-sm font-semibold">Send the Weekly Digest to <span className="font-mono-tt text-[var(--tt-primary)]">{user.email}</span></div>
                  <div className="text-[11px] text-[var(--tt-text-muted)]">
                    {digestOptIn === null
                      ? "Loading…"
                      : digestOptIn
                        ? "Subscribed — first email will arrive next Monday."
                        : "Off — turn this on to start receiving the digest."}
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={Boolean(digestOptIn)}
                  disabled={digestBusy || digestOptIn === null}
                  onChange={(e) => toggleDigest(e.target.checked)}
                  data-testid="settings-digest-toggle"
                  className="w-5 h-5 accent-[var(--tt-primary)] flex-none"
                />
              </label>
            </section>
          )}

          {/* Linked TV devices */}
          {user && (
            <section className="mb-8 bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl p-5" data-testid="settings-devices-card">
              <h2 className="font-display text-lg font-bold mb-1 flex items-center gap-2">
                <Tv className="w-5 h-5 text-[var(--tt-primary)]" /> Linked TVs &amp; devices
              </h2>
              <p className="text-xs text-[var(--tt-text-muted)] mb-4">
                Fire TVs, Android TVs and Cubes you&apos;ve signed-in to with a pairing code. Revoke any device you no longer recognise.
              </p>

              <Link
                to="/link"
                data-testid="settings-pair-device-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white text-sm font-bold mb-4"
              >
                <Plus className="w-4 h-4" /> Pair a new device
              </Link>

              {devices === null ? (
                <div className="text-xs text-[var(--tt-text-muted)] flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>
              ) : devices.length === 0 ? (
                <div className="text-xs text-[var(--tt-text-faint)] italic">No devices linked yet.</div>
              ) : (
                <ul className="space-y-2" data-testid="settings-devices-list">
                  {devices.map((d) => (
                    <li
                      key={d.device_id}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-[var(--tt-surface-2)] border border-[var(--tt-border)]"
                      data-testid={`settings-device-${d.device_id}`}
                    >
                      <Tv className="w-4 h-4 text-[var(--tt-primary)] flex-none" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold truncate">{d.device_name || "Fire TV"}</div>
                        <div className="text-[10px] text-[var(--tt-text-faint)] font-mono-tt truncate">
                          {(d.platform || "android_tv")}{d.device_model ? ` · ${d.device_model}` : ""}
                          {d.linked_at ? ` · linked ${d.linked_at.slice(0, 10)}` : ""}
                        </div>
                      </div>
                      <button
                        onClick={() => revokeDevice(d.device_id, d.device_name)}
                        disabled={devicesBusy}
                        data-testid={`settings-device-revoke-${d.device_id}`}
                        className="text-[11px] uppercase font-bold px-2.5 py-1 rounded bg-red-500/10 text-red-400 hover:bg-red-500/20 flex items-center gap-1 disabled:opacity-40"
                      >
                        <Trash2 className="w-3 h-3" /> Sign out
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {/* Codec */}
          <section className="mb-8 bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl p-5" data-testid="settings-codec-card">
            <h2 className="font-display text-lg font-bold mb-1 flex items-center gap-2">
              <Film className="w-5 h-5 text-[var(--tt-yellow)]" /> Preferred codec
            </h2>
            <p className="text-xs text-[var(--tt-text-muted)] mb-4">
              Helpful when your device only supports specific codecs (e.g. Apple TV: HEVC, older NAS: H.264).
            </p>

            <label
              className="flex items-center justify-between gap-3 px-4 py-3 mb-3 rounded-lg bg-[var(--tt-surface-2)] border border-[var(--tt-border)] cursor-pointer hover:border-[var(--tt-yellow)]/50 transition-colors"
              data-testid="settings-auto-hevc"
            >
              <div>
                <div className="text-sm font-semibold">Always prefer HEVC / H.265</div>
                <div className="text-[11px] text-[var(--tt-text-muted)]">Smallest files at the same quality. Best for streaming.</div>
              </div>
              <input
                type="checkbox"
                checked={autoHevc}
                onChange={(e) => onAutoHevc(e.target.checked)}
                className="w-5 h-5 accent-[var(--tt-yellow)]"
              />
            </label>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {CODECS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  data-testid={`settings-codec-${c.value.toLowerCase()}`}
                  onClick={() => onCodec(c.value)}
                  className={`px-3 py-2.5 rounded-lg border text-sm text-left transition-colors ${
                    codec === c.value
                      ? "bg-[var(--tt-yellow)]/20 border-[var(--tt-yellow)] text-[var(--tt-yellow)]"
                      : "bg-[var(--tt-surface-2)] border-[var(--tt-border)] text-[var(--tt-text)] hover:border-[var(--tt-yellow)]/40"
                  }`}
                >
                  <div className="font-bold">{c.label}</div>
                  {c.hint && (
                    <div className="text-[10px] text-[var(--tt-text-muted)] mt-0.5 leading-tight">{c.hint}</div>
                  )}
                </button>
              ))}
            </div>
          </section>

          <IptvSourcesSection />


          {/* Quality floor — hide trash rips invisibly on every title */}
          <section
            className="mb-8 bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl p-5"
            data-testid="settings-qualityfloor-card"
          >
            <h2 className="font-display text-lg font-bold mb-1 flex items-center gap-2">
              <EyeOff className="w-5 h-5 text-red-400" /> Quality floor
            </h2>
            <p className="text-xs text-[var(--tt-text-muted)] mb-4">
              Hide trash rips before they ever reach your screen. Applies to every title automatically — no chip-clicking required.
            </p>

            <label
              className="flex items-center justify-between gap-3 px-4 py-3 mb-2 rounded-lg bg-[var(--tt-surface-2)] border border-[var(--tt-border)] cursor-pointer hover:border-red-400/50 transition-colors"
              data-testid="settings-hide-cam-row"
            >
              <div>
                <div className="text-sm font-semibold">Hide CAM / TS / HDCAM rips</div>
                <div className="text-[11px] text-[var(--tt-text-muted)]">
                  Filters out cinema-camera bootlegs and telesync rips. Recommended.
                </div>
              </div>
              <input
                type="checkbox"
                checked={hideCam}
                onChange={(e) => onHideCam(e.target.checked)}
                data-testid="settings-hide-cam-toggle"
                className="w-5 h-5 accent-red-400 flex-none"
              />
            </label>

            <label
              className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg bg-[var(--tt-surface-2)] border border-[var(--tt-border)] cursor-pointer hover:border-red-400/50 transition-colors"
              data-testid="settings-hide-sd-row"
            >
              <div>
                <div className="text-sm font-semibold">Hide SD (DVDRip / 480p)</div>
                <div className="text-[11px] text-[var(--tt-text-muted)]">
                  Hide standard-definition rips. Off by default — useful if you want small files for phones / older devices.
                </div>
              </div>
              <input
                type="checkbox"
                checked={hideSd}
                onChange={(e) => onHideSd(e.target.checked)}
                data-testid="settings-hide-sd-toggle"
                className="w-5 h-5 accent-red-400 flex-none"
              />
            </label>
          </section>

          {/* Action bar */}
          <div className="sticky bottom-0 bg-black/80 backdrop-blur-md border-t border-[var(--tt-border)] -mx-4 md:-mx-8 px-4 md:px-8 py-3 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={resetAll}
              data-testid="settings-reset"
              className="text-xs font-semibold px-3 py-2 rounded-lg bg-[var(--tt-surface)] hover:bg-[var(--tt-surface-2)] border border-[var(--tt-border)] text-[var(--tt-text-muted)] hover:text-white flex items-center gap-1.5"
            >
              <Eraser className="w-3.5 h-3.5" /> Reset everything
            </button>
            <div className="flex items-center gap-3">
              {dirty && (
                <span className="text-[11px] text-[var(--tt-text-muted)] italic">Unsaved changes</span>
              )}
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="text-sm font-semibold px-4 py-2 rounded-lg bg-transparent hover:bg-white/5 text-[var(--tt-text-muted)]"
                data-testid="settings-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                disabled={!dirty}
                data-testid="settings-save"
                className={`text-sm font-bold px-5 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                  dirty
                    ? "bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white"
                    : "bg-[var(--tt-surface-2)] text-[var(--tt-text-muted)] cursor-not-allowed"
                }`}
              >
                <Save className="w-4 h-4" /> Save
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </div>
  );
}
